import {
    Component,
    Input,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { FieldsetToggleComponent } from "@components/fieldset-toggle/fieldset-toggle.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { MarknadsomradeDTO } from "~app/common/models/common/MarknadsomradeDTO";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ITplArbetsorder } from "../../../models/dtos";
import { ITplProduktionsomradefilterQueryResult, TplProduktionsomradefilterQuery } from "../../../models/queries";
import { TplService } from "../../../services/tpl.service";
import { FilterBaseComponent } from "../filter-base-component";
import { TplFilterContext } from "../tpl-filter-context";
import { ITplArbetsorderFilter } from "../tpl-filter.models";

@Component({
    selector: "rask-tpl-produktionsomrade-filter",
    templateUrl: "./tpl-produktionsomrade-filter.component.html",
    styleUrls: ["./tpl-produktionsomrade-filter.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FieldsetToggleComponent,
        FormsModule,
        ReactiveFormsModule,
        TreeSelectorComponent
    ]
})

export class TplProduktionsomradeFilterComponent extends FilterBaseComponent implements OnInit, ITplArbetsorderFilter {
    @Input() dataSource: ITplArbetsorder[];
    marknadsomraden: SelectionItem[];

    private readonly tplService = inject(TplService);

    constructor() {
        super("produktionsomrade");
    }

    initProduktionsomradeFilter() {
        const initialState: number[] = this.filterContext.filterState[this.name] ?? [];

        if (!(this.dataSource?.length)) {
            return;
        }

        this.tplService.loadProduktionsomradeFilter(new TplProduktionsomradefilterQuery(this.dataSource))
            .subscribe((result: ITplProduktionsomradefilterQueryResult) => {
                this.initProduktionsomraden(result.marknadsomraden);
                this.setSelectedProduktionsomraden(initialState);
                this.open = this.isActive();
            });
    }

    isActive(): boolean {
        return this.value().length > 0;
    }

    match(arbetsorder: ITplArbetsorder): boolean {
        return this.value().includes(arbetsorder.produktionsomradeId);
    }

    ngOnInit(): void {
        this.onAddFilterToContext(this.filterContext);
        this.initProduktionsomradeFilter();
    }

    selectionChanged() {
        const values = this.value();
        this.filterLength = values.length;
    }

    value(): number[] {
        return this.getSelectedProduktionsomradeIdList();
    }

    protected onAddFilterToContext(filterContext: TplFilterContext) {
        filterContext.addArbetsorderFilter(this.name, this);
    }

    protected onFilterInit(state: number[]): void {
        if (!state || !state.length) {
            this.clearSelectedProduktionsomraden();
            return;
        }

        this.setSelectedProduktionsomraden(state);
    }

    protected onFormChange(value: number[]): void {
        // empty
    }

    private readonly clearSelectedProduktionsomraden = () => {
        const moitems = this.marknadsomraden;
        if (!moitems) {
            return;
        }

        moitems.forEach(mo => {
            mo.selected = false;
            mo.partialSelected = false;

            mo.children?.forEach(ro => {
                ro.selected = false;
                ro.partialSelected = false;

                ro.children?.forEach(po => {
                    po.selected = false;
                    po.partialSelected = false;
                });
            });
        });

        this.selectionChanged();
        this.filterContext.filterState[this.name] = [];
    };

    private readonly getSelectedProduktionsomradeIdList = () => {
        const moitems = this.marknadsomraden;
        const poids: number[] = [];
        if (!moitems) {
            return null;
        }

        moitems.forEach(mo => {
            mo.children
                .filter(ro => ro.selected || ro.partialSelected)
                .forEach(ro => {
                    ro.children
                        .filter(po => po.selected)
                        .forEach(po => poids.push(po.id));
                });
        });

        return poids;
    };

    private initProduktionsomraden(raw: MarknadsomradeDTO[]) {
        this.marknadsomraden = raw.map(marknadsOmrade => ({
            id: marknadsOmrade.id,
            text: marknadsOmrade.namn,
            children: marknadsOmrade.children.map(resultatOmrade => ({
                id: resultatOmrade.id,
                text: resultatOmrade.namn,
                children: resultatOmrade.children.map(produktionsOmrade => ({
                    id: produktionsOmrade.id,
                    text: produktionsOmrade.namn
                }))
            }))
        } as SelectionItem));
    }

    private readonly setSelectedProduktionsomraden = (filter: number[]) => {
        const moitems = this.marknadsomraden;
        if (!ObjectUtils.hasValue(filter) || !ObjectUtils.hasValue(moitems)) {
            return;
        }

        moitems.forEach(mo => {
            mo.children.forEach(ro => {
                ro.children.forEach(po => {
                    if(filter.includes(po.id)) {
                        po.selected = true;
                    }
                });
                const allPoSelected = ro.children.length >0 && ro.children.every(po => po.selected);
                ro.selected = allPoSelected;
                ro.partialSelected = !allPoSelected && ro.children.some(po => po.selected);
            });
            const allRoSelected = mo.children.length >0 && mo.children.every(ro => ro.selected);
            mo.selected = allRoSelected;
            mo.partialSelected = !allRoSelected && mo.children.some(ro => ro.selected || ro.partialSelected);
        });
    };
}
